<template>
  <div class="predmeti">
    <div class="predmeti__toolbar">
      <div class="segmented">
        <button
          :class="['segmented__btn', { 'is-active': activeName === 'first' }]"
          @click="selectTab('first')"
        >
          Otvoreni
          <span v-if="counts.open !== null" class="segmented__count">{{ counts.open }}</span>
        </button>
        <button
          :class="['segmented__btn', { 'is-active': activeName === 'second' }]"
          @click="selectTab('second')"
        >
          Zatvoreni
          <span v-if="counts.closed !== null" class="segmented__count">{{ counts.closed }}</span>
        </button>
      </div>

      <div class="search">
        <i class="el-icon-search search__icon"></i>
        <input
          v-model="searchQtmp"
          class="search__input"
          placeholder="Pretraga: broj, oštećeni, registarska oznaka…"
          @change="handleSearch"
          @keyup.enter="handleSearch"
        />
        <i v-if="searchQtmp" class="el-icon-close search__clear" @click="clearSearch"></i>
      </div>

      <div class="spacer"></div>

      <router-link to="/predmet/create">
        <el-button type="primary" icon="el-icon-plus">Novi predmet</el-button>
      </router-link>
    </div>

    <div class="predmeti__filters">
      <el-select
        v-if="activeName === 'first'"
        v-model="formData.status"
        class="filter-select"
        size="small"
        filterable
        placeholder="Faza"
        @change="handleStatusFilter"
      >
        <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-select
        v-model="formData.obaveza"
        class="filter-select filter-select--wide"
        size="small"
        filterable
        placeholder="Obaveza"
        @change="handleObavezaFilter"
      >
        <el-option v-for="item in formContent.obaveza" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-select
        v-model="formData.saradnik"
        class="filter-select filter-select--wide"
        size="small"
        filterable
        clearable
        placeholder="Saradnik"
        @change="handleSaradnikFilter"
      >
        <el-option v-for="item in formContent.saradnik" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-select
        v-model="formData.nosioc"
        class="filter-select"
        size="small"
        clearable
        placeholder="Nosioc"
        @change="handleNosiocFilter"
      >
        <el-option v-for="item in formContent.nosioc" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>

      <el-checkbox v-model="numOnly" class="filter-check" @change="handleNumOnly">Samo po broju</el-checkbox>

      <div class="chips">
        <span v-for="chip in activeChips" :key="chip.key" class="chip">
          {{ chip.label }}
          <i class="el-icon-close chip__x" @click="chip.clear()"></i>
        </span>
        <button v-if="activeChips.length > 1" class="chips__reset" @click="clearAllFilters">
          Poništi sve
        </button>
      </div>
    </div>

    <div class="predmeti__workspace">
      <PredmetList
        v-show="activeName === 'first'"
        class="predmeti__list"
        :filter="{ status: { op: 'eq', val: formData.status } }"
        :search="queryValue"
        :numonlymode="queryFilterNumOnly"
        :fobv="formData.obaveza"
        :fsar="formData.saradnik"
        :fnos="formData.nosioc"
        :waitload="waitLoad"
        :selected-id="selectedId"
        @select="onSelect"
        @count="counts.open = $event"
      />
      <PredmetList
        v-show="activeName === 'second'"
        class="predmeti__list"
        :filter="{ status: { op: 'eq', val: 10 } }"
        :search="queryValue"
        :numonlymode="queryFilterNumOnly"
        :fobv="formData.obaveza"
        :fsar="formData.saradnik"
        :fnos="formData.nosioc"
        :waitload="waitLoad"
        :selected-id="selectedId"
        @select="onSelect"
        @count="counts.closed = $event"
      />
      <PredmetRail :predmet-id="selectedId" />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import API from "@/services/api/api";
import { loadNosioci } from "@/models/nosioc";
import PredmetList from "@/components/PredmetList";
import PredmetRail from "@/components/PredmetRail";
import { statusFilterOptions, getStatusLabel } from "@/models/status";

export default {
  name: "Predmeti",
  components: { PredmetList, PredmetRail },
  data() {
    return {
      waitLoad: true,
      activeName: this.predmetiActiveName,
      searchQ: this.queryValue,
      numOnly: this.queryFilterNumOnly,
      searchQtmp: this.queryValue,
      selectedId: 0,
      counts: { open: null, closed: null },
      statusOptions: statusFilterOptions(),
      formData: {
        obaveza: this.searchObavezaFilter,
        status: this.searchStatusFilter,
        saradnik: this.searchSaradnikFilter,
        nosioc: this.searchNosiocFilter,
      },
      formContent: {
        obaveza: [],
        saradnik: [],
        nosioc: [],
      },
    };
  },
  computed: {
    ...mapGetters([
      "queryValue",
      "queryFilterNumOnly",
      "predmetiActiveName",
      "searchStatusFilter",
      "searchObavezaFilter",
      "searchSaradnikFilter",
      "searchNosiocFilter",
    ]),
    nosiocLabel() {
      const match = this.formContent.nosioc.find((o) => o.value === this.formData.nosioc);
      return match ? match.label : "";
    },
    saradnikLabel() {
      const match = this.formContent.saradnik.find((o) => o.value === this.formData.saradnik);
      return match ? match.label : "";
    },
    obavezaLabel() {
      const match = this.formContent.obaveza.find((o) => o.value === this.formData.obaveza);
      return match ? match.label : "";
    },
    // Every filter currently narrowing the list, as removable chips.
    activeChips() {
      const chips = [];
      if (this.queryValue) {
        chips.push({
          key: "q",
          label: `Pretraga: „${this.queryValue}”`,
          clear: this.clearSearch,
        });
      }
      if (this.activeName === "first" && this.formData.status !== -1) {
        chips.push({
          key: "status",
          label: `Faza: ${getStatusLabel(this.formData.status)}`,
          clear: () => this.handleStatusFilter(-1),
        });
      }
      if (this.formData.obaveza !== -1) {
        chips.push({
          key: "obaveza",
          label: `Obaveza: ${this.obavezaLabel}`,
          clear: () => this.handleObavezaFilter(-1),
        });
      }
      if (this.formData.nosioc !== -1) {
        chips.push({
          key: "nosioc",
          label: `Nosioc: ${this.nosiocLabel}`,
          clear: () => this.handleNosiocFilter(-1),
        });
      }
      if (this.formData.saradnik !== -1) {
        chips.push({
          key: "saradnik",
          label: `Saradnik: ${this.saradnikLabel}`,
          clear: () => this.handleSaradnikFilter(-1),
        });
      }
      if (this.queryFilterNumOnly) {
        chips.push({
          key: "numonly",
          label: "Samo po broju",
          clear: () => this.handleNumOnly(false),
        });
      }
      return chips;
    },
  },
  methods: {
    ...mapActions([
      "setQuery",
      "toggleFilterNumOnly",
      "togglePredmetiActiveName",
      "updateStatusFilter",
      "updateObavezaFilter",
      "updateSaradnikFilter",
      "updateNosiocFilter",
    ]),
    selectTab(name) {
      if (this.activeName === name) return;
      this.activeName = name;
      this.selectedId = 0;
      this.togglePredmetiActiveName();
    },
    onSelect(row) {
      this.selectedId = row.id;
    },
    handleStatusFilter(e) {
      this.formData.status = e;
      this.selectedId = 0;
      this.updateStatusFilter(e);
    },
    handleNosiocFilter(e) {
      const v = e === "" || e === undefined || e === null ? -1 : e;
      this.formData.nosioc = v;
      this.selectedId = 0;
      this.updateNosiocFilter(v);
    },
    handleSaradnikFilter(e) {
      const v = e === "" || e === undefined || e === null ? -1 : e;
      this.formData.saradnik = v;
      this.selectedId = 0;
      this.updateSaradnikFilter(v);
    },
    handleObavezaFilter(e) {
      this.formData.obaveza = e;
      this.selectedId = 0;
      this.updateObavezaFilter(e);
    },
    handleSearch() {
      this.searchQ = this.searchQtmp;
      // The previewed case may not survive the new result set.
      this.selectedId = 0;
      this.setQuery(this.searchQtmp);
    },
    clearSearch() {
      this.searchQtmp = "";
      this.handleSearch();
    },
    handleNumOnly(e) {
      this.numOnly = e;
      this.selectedId = 0;
      if (this.queryFilterNumOnly !== e) this.toggleFilterNumOnly();
    },
    clearAllFilters() {
      this.clearSearch();
      this.handleStatusFilter(-1);
      this.handleObavezaFilter(-1);
      this.handleSaradnikFilter(-1);
      this.handleNosiocFilter(-1);
      this.handleNumOnly(false);
    },
    loadSaradnik: async function () {
      return API.Generic.all("saradnik", { active: { op: "eq", val: true } })
        .then((data) => {
          this.formContent.saradnik = [{ value: -1, label: "Saradnik — svi" }].concat(
            data.data.data.results.map((o) => ({
              value: o.id,
              label: [o.ime, o.prezime].filter(Boolean).join(" "),
            }))
          );
        })
        .catch((err) => console.log(err));
    },
    loadObaveza: async function () {
      return API.Generic.all("obaveza")
        .then((data) => {
          let d = data.data.data;
          this.formData.obaveza = this.searchObavezaFilter;
          this.formContent.obaveza = [{ value: -1, label: "Obaveza — sve" }];
          d.results.forEach((o) => {
            this.formContent.obaveza.push({ value: o.id, label: o.naziv });
          });
        })
        .catch((err) => {
          console.log(err);
        });
    },
  },
  beforeMount() {
    this.formData.status = this.searchStatusFilter;
    this.formData.obaveza = this.searchObavezaFilter;
    this.formData.saradnik = this.searchSaradnikFilter;
    this.formData.nosioc = this.searchNosiocFilter;
    this.activeName = this.predmetiActiveName;
    this.numOnly = this.queryFilterNumOnly;
    this.searchQtmp = this.queryValue;
    this.searchQ = this.queryValue;
    this.waitLoad = false;
  },
  created() {
    this.waitLoad = true;
    this.loadObaveza();
    this.loadSaradnik();
    loadNosioci()
      .then((list) => {
        this.formContent.nosioc = [{ value: -1, label: "Nosioc — svi" }].concat(
          list.map((n) => ({ value: n.id, label: n.naziv }))
        );
      })
      .catch((e) => console.log(e));
  },
};
</script>

<style lang="scss" scoped>
.predmeti {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  gap: 10px;
}

.predmeti__toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.spacer {
  flex-grow: 1;
}

.segmented {
  display: flex;
  border: 1px solid var(--line-strong);
  border-radius: var(--r-md);
  overflow: hidden;
  flex-shrink: 0;
}

.segmented__btn {
  padding: 7px 15px;
  background: var(--surface);
  border: none;
  font: inherit;
  font-size: var(--fs-md);
  font-weight: 500;
  color: var(--ink-2);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 7px;

  &.is-active {
    background: var(--navy);
    color: #fff;
  }

  & + & {
    border-left: 1px solid var(--line-strong);
  }
}

.segmented__count {
  font-size: var(--fs-sm);
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
}

.search {
  position: relative;
  flex-grow: 1;
  max-width: 460px;
  display: flex;
  align-items: center;
}

.search__icon,
.search__clear {
  position: absolute;
  color: var(--ink-3);
  font-size: var(--fs-lg);
}

.search__icon {
  left: 11px;
}

.search__clear {
  right: 10px;
  cursor: pointer;
}

.search__input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 30px 8px 32px;
  border: 1px solid var(--line);
  background: var(--surface-alt);
  border-radius: var(--r-md);
  font: inherit;
  font-size: var(--fs-md);
  color: var(--ink);
  outline: none;

  &:focus {
    border-color: var(--brand);
    background: var(--surface);
    box-shadow: 0 0 0 3px rgba(47, 111, 237, 0.13);
  }

  &::placeholder {
    color: var(--ink-4);
  }
}

.predmeti__filters {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.filter-select {
  width: 190px;
}

.filter-select--wide {
  width: 220px;
}

.filter-check {
  font-size: var(--fs-md);
}

.chips {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 10px;
  background: var(--brand-tint);
  border: 1px solid var(--brand-line);
  border-radius: 999px;
  font-size: var(--fs-sm);
  color: var(--brand-ink);
  white-space: nowrap;
}

.chip__x {
  cursor: pointer;
  font-size: var(--fs-sm);
}

.chips__reset {
  background: none;
  border: none;
  font: inherit;
  font-size: var(--fs-sm);
  color: var(--ink-3);
  text-decoration: underline;
  cursor: pointer;
  padding: 0 2px;
}

.predmeti__workspace {
  display: flex;
  flex-grow: 1;
  min-height: 0;
}

.predmeti__list {
  flex-grow: 1;
  min-width: 0;
}
</style>
